import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";
import _ from "lodash";
import { Box } from "@mui/material";
import { MyHrAddMassRole, MyHrBackToPage } from "my-hr/components";
import {
  addMassRoleEmployeeList,
  addMassRoleEmployeeListIsMobile,
} from "my-hr/configs/my-hr-table-defs";
import { useMyHrFilteredList, useMyHrFilteredListCount } from "my-hr/hooks";
import {
  OcCard,
  OcDataTable,
  OcPageTransition,
  OcRoleFilter,
} from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcButtonGroup, useOcTableState, useOcIsMobile } from "oc/hooks";
import { myHrRoleFilterState, ocRoleFilteringState } from "states";

export const MyHRAdminAddGroupRolesPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();

  const [roleFilters, setRoleFilters] = useRecoilState(myHrRoleFilterState);
  const [roleFiltering, setRoleFiltering] =
    useRecoilState(ocRoleFilteringState);

  const buttonDefs = ["OC_ACTIVE", "OC_ALL"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "MyHRAdminAddGroupRolesPageButtonGroup",
    buttonsDef: buttonDefs,
  });

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  let tableId = "filteredListTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    pageSize: 5,
  });

  const { data: filteredListCount } = useMyHrFilteredListCount({
    isActive: selectedButton === "OC_ACTIVE" ? true : false,
    roleFilters,
    useOrLogic: true,
  });

  const { data: filteredList, loading } = useMyHrFilteredList({
    page: tableData.page,
    pageSize: tableData.pageSize,
    isActive: selectedButton === "OC_ACTIVE" ? true : false,
    roleFilters,
    useOrLogic: true,
  });

  const handleAddFilter = ({ key, value }: { key: string; value: any }) => {
    const newState = _.cloneDeep(roleFilters);
    newState[key] = newState[key].concat([value]);
    setRoleFilters(newState);
  };

  const handleRemoveFilter = ({ key, value }: { key: string; value: any }) => {
    const newState = _.cloneDeep(roleFilters);
    newState[key] = newState[key].filter((item: any) => item !== value);
    setRoleFilters(newState);
  };

  const handleAddFiltering = ({ key, value }: { key: string; value: any }) => {
    const newState = _.cloneDeep(roleFiltering);
    newState[key] = newState[key].concat([value]);
    setRoleFiltering(newState);
  };

  const handleRemoveFiltering = ({
    key,
    value,
  }: {
    key: string;
    value: any;
  }) => {
    const newState = _.cloneDeep(roleFiltering);
    newState[key] = newState[key].filter((item: any) => item !== value);
    setRoleFiltering(newState);
  };

  const resetRoleFilters = useResetRecoilState(myHrRoleFilterState);

  const handleCancel = () => {
    resetRoleFilters();
    history.push("/my-hr/users");
  };

  const tableDef = isMobile
    ? addMassRoleEmployeeListIsMobile
    : addMassRoleEmployeeList;

  const filteredTableDef = {
    id: addMassRoleEmployeeList?.id,
    columns: addMassRoleEmployeeList?.columns?.filter(
      (column) =>
        !addMassRoleEmployeeListIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const onSelectRow = (o: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? undefined : o?._id);
    }
  };

  React.useEffect(() => {
    return () => {
      resetRoleFilters();
    };
  }, []); // eslint-disable-line

  return (
    <OcPageTransition>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        height="100%"
      >
        <Box padding={isMobile ? "16px 0px" : "16px"}>
          <MyHrBackToPage
            label={t("MY_HR_ADD_GROUP_ROLE_TITLE")}
            onBack={handleCancel}
            isMobile={isMobile}
          />
          <Box height="16px" />
          <OcCard label={t("MY_HR_USERS")}>
            <>
              <OcRoleFilter
                subtypeProps={{
                  queryType: "admin",
                  handleAddItem: handleAddFilter,
                  handleRemoveItem: handleRemoveFilter,
                  roleFilters: roleFilters,
                  product: "MyHR",
                  role: "admin",
                }}
                OcButtonGroup={OcButtonGroup}
                summaryItems={[
                  {
                    label: "OC_COUNT_OF_EMPLOYEES",
                    value: filteredListCount,
                    color: "primary",
                  },
                ]}
                buttonBarProps={{
                  handleRightButton: resetRoleFilters,
                  primaryButtonText: "",
                  noLine: true,
                  paddingTop: "10px",
                }}
              />
              <Box paddingTop="32px">
                <OcDataTable
                  data={filteredList}
                  loading={loading}
                  tableDef={tableDef}
                  tableData={tableData}
                  setTableData={setTableData}
                  pagination={true}
                  onSelectRow={onSelectRow}
                  filteredTableDef={filteredTableDef}
                  selectedRowId={selectedRowId}
                />
              </Box>
              <MyHrAddMassRole
                roleFilters={roleFilters}
                roleFiltering={roleFiltering}
                onAddFiltering={handleAddFiltering}
                onRemoveFiltering={handleRemoveFiltering}
                isMobile={isMobile}
              />
            </>
          </OcCard>
        </Box>
      </Box>
    </OcPageTransition>
  );
};

export default MyHRAdminAddGroupRolesPage;
